import { Businesses, Users } from "@just-in-types";
import { navigate } from "@reach/router";
import { isToday } from "date-fns";
import { any, string } from "prop-types";
import { AnyAction, Dispatch } from "redux";
import store from "..";
import firebase from "../../firebase";
import { firestore, storage } from "../../firebase";
import { IState } from "../reducers";
import { isRouteString, SET_LANGUAGE } from "./routerActions";

export interface IUserCheckInData {
  language: Users.User["language"];
  mobile: Users.User["mobile"];
  name: Users.User["name"];
  signature: Users.User["signature"];
  userId: Users.User["userId"];
  vehicles: Users.User["vehicles"];
  zip?: Users.User["zip"];
}

export interface IIDs {
  businessId: Businesses.Business["businessId"];
  checkInId: Users.CheckIn["checkInId"];
  siteId: Businesses.Site["siteId"];
  userId: Users.User["userId"];
}

export interface IUserValueObject {
  name: UserValue["name"];
  value: UserValue["value"];
}

export type UserValue =
  | { name: "hasAccount"; value: boolean }
  | { name: "dateofbirth"; value: Date }
  | { name: "waiver"; value: string }
  | {
      name:
        | "receiptCode"
        | "email"
        | "language"
        | "mobile"
        | "mugshot"
        | "mugshotUrl"
        | "signature"
        | "signatureUrl"
        | "supercard"
        | "waiver"
        | "zip"
        | "customerType"
        | "covidquestions"
        | "optin";
      value: string;
    }
  | {
      name: "name";
      value: { displayName: string; firstname: string; lastname: string };
    }
  | { name: "vehicles"; value: Users.VehiclePick[] };

const checkInsRef = firestore.collection("checkIns");
const usersRef = firestore.collection("users");

export const emptyUser: Users.EmptyUser = {
  covidquestions: null,
  dateofbirth: null,
  email: null,
  hasAccount: false,
  hasPaidToday: false,
  language: null,
  mobile: null,
  mugshot: null,
  mugshotUrl: null,
  name: { displayName: null, firstname: null, lastname: null },
  receiptCode: null,
  secretMobile: null,
  signature: null,
  signatureUrl: null,
  skipCheckIn: false,
  supercard: null,
  vehicles: [],
  zip: null,
};

const getImageUrl = async (
  imageData: string,
  fileName: "mugshot.jpeg" | "signature.png",
  { businessId, checkInId, siteId, userId }: IIDs
) => {
  if (imageData) {
    const imageRef = storage.ref(`/checkIns/${businessId}/${checkInId}/${fileName}`);
    try {
      const uploadSnapshot = await imageRef.putString(imageData, "data_url", {
        customMetadata: { businessId, checkInId, siteId, userId },
      });

      const imageUrl: string = await uploadSnapshot.ref.getDownloadURL();

      return imageUrl;
    } catch (error) {
      throw new Error(error);
    }
  } else {
    return null;
  }
};

const addImageUrlsToUser = async (user: Users.User, ids: IIDs) => {
  const { mugshot, signature } = user;
  const imagePromises: Array<Promise<string | null>> = [];

  if (mugshot) {
    imagePromises.push(getImageUrl(mugshot, "mugshot.jpeg", ids));
  }
  if (signature) {
    imagePromises.push(getImageUrl(signature, "signature.png", ids));
  }

  const [mugshotUrl, signatureUrl] = await Promise.all(imagePromises);

  user.mugshotUrl = mugshotUrl || null;
  user.signatureUrl = signatureUrl || null;

  return user;
};

const updateUser = async ({ businessId, checkInId, siteId, userId }: IIDs) => {
  const user = store.getState().user as Users.User;
  console.log(user);
  const userWithUrls = await addImageUrlsToUser(user, {
    businessId,
    checkInId,
    siteId,
    userId,
  });
  const userRef = usersRef.doc(userId);

  try {
    await userRef.set(userWithUrls, { merge: true });

    return userWithUrls;
  } catch (error) {
    throw new Error(error);
  }
};

export type AddCheckIn = (partialCheckIn: {
  businessId: string;
  signature?: string;
  siteId: string;
  userId: string;
}) => (dispatch: Dispatch<AnyAction>) => Promise<boolean>;
export const ADD_CHECKIN = "user.ADD_CHECKIN";
export const addCheckIn: AddCheckIn =
  ({ businessId, siteId, userId }) =>
  async (dispatch: Dispatch) => {
    try {
      const {
        router: { businessPath, noCheckIn, sitePath },
      } = store.getState() as IState;

      console.log("ADDING CHECK IN ---------------------------------------------");
      const checkInRef = checkInsRef.doc();
      const checkInId = checkInRef.id;
      const {
        dateofbirth,
        covidquestions,
        email,
        language,
        mobile,
        mugshot,
        mugshotUrl,
        optin,
        name,
        receiptCode,
        signature,
        signatureUrl,
        supercard,
        vehicles,
        waiver,
        zip,
      } = await updateUser({ businessId, checkInId, siteId, userId });
      if (waiver) {
        console.log("waiver from update: ");
        console.log(waiver);
      }

      const optedIn = optin === undefined ? null : optin;
      if (!noCheckIn) {
        const checkInObject = {
          businessId,
          businessPath,
          covidquestions,
          dateofbirth,
          email,
          language,
          mobile,
          mugshot: null,
          mugshotUrl,
          name,
          optin: optedIn,
          receiptCode,
          signature: null,
          signatureUrl,
          siteId,
          sitePath,
          supercard: supercard ? supercard : null,
          timestamp: new Date(),
          userId,
          vehicles,
          waiver,
          zip,
        };

        for (const field in checkInObject) {
          if (field === undefined) {
            checkInObject[field] = null;
          }
        }
        if (!checkInObject.supercard) {
          checkInObject.supercard = null;
        }
        if (checkInObject.supercard === undefined) {
          checkInObject.supercard = null;
        }
        if (checkInObject.covidquestions === undefined) {
          checkInObject.covidquestions = null;
        }

        await checkInRef.set(checkInObject), dispatch({ type: ADD_CHECKIN });
      }

      return false;
    } catch (error) {
      throw new Error(error);
    }
  };

export const SET_USER_OPTED_OUT = "user.SET_USER_OPTED_OUT";
export const setUserOptedOut = (lan = 'eng') => {
  const mobile = "6028181729";

  store.dispatch({
    payload: {
      ...emptyUser,
      lanOpt: lan === "eng" ? "eng" : "esp",
      mobile,
      secretMobile: "opt-out",
      userId: "opt-out",
    },
    type: SET_USER_OPTED_OUT,
  });
};

export type SetUserValue = (
  { name, value }: IUserValueObject,
  mobile?: string,
  businessId?: string
) => (dispatch: Dispatch<AnyAction>) => Promise<void>;

export const SET_USER_VALUE = "user.SET_USER_VALUE";
export const setUserValue =
  ({ name, value }: UserValue, mobile?: string, businessId?: string) =>
  async (dispatch: Dispatch) => {
    const {
      user: { userId },
      userTemp: { updating },
    } = store.getState() as IState;

    try {
      if (updating && userId && isRouteString(name)) {
        firestore
          .collection("users")
          .doc(userId)
          .update({ [name]: value });

        if (mobile && businessId) {
          // update most recent checkIn as well
          firestore
            .collection("checkIns")
            .where("mobile", "==", mobile)
            .where("businessId", "==", businessId)
            .orderBy("timestamp", "desc")
            .limit(1)
            .get()
            .then((query) => {
              query.docs[0].ref.update({ [name]: value });
            });
        }
      } else {
        if (userId && isRouteString(name)) {
          if (["zip", "name", "dateofbirth"].includes(name) && userId !== "opt-out") {
            firestore
              .collection("users")
              .doc(userId)
              .update({ [name]: value });
            firestore
              .collection("checkIns")
              .where("mobile", "==", mobile)
              .where("businessId", "==", businessId)
              .orderBy("timestamp", "desc")
              .limit(1)
              .get()
              .then((query) => {
                if (!query.empty) {
                  query.docs[0].ref.update({ [name]: value });
                }
              });
          }
        }
      }

      dispatch({ type: SET_USER_VALUE, payload: { name, value } });
    } catch (error) {
      throw new Error(error);
    }
  };

export type AddVehicle = (
  vehicle: Users.VehiclePick,
  user: Users.User
) => (dispatch: Dispatch<AnyAction>) => {
  type: string;
  payload: Users.VehiclePick;
};
export const ADD_VEHICLE = "user.ADD_VEHICLE";
export const addVehicle = (vehicle: Users.VehiclePick, user: Users.User) => (dispatch: Dispatch) => {
  const make = vehicle.make.toUpperCase();
  const model = vehicle.model.toUpperCase();
  const combined = make + " " + model;
  firestore.collection("vehiclePicks").add({
    businessId: vehicle.businessId,
    combined,
    firstName: user.name.firstname,
    language: vehicle.language ? vehicle.language : null,
    make,
    mobile: user.mobile,
    model,
    siteId: vehicle.siteId,
    textSent: false,
    userId: user.userId,
    yearMax: vehicle.yearMax,
    yearMin: vehicle.yearMin,
  });
  dispatch({ type: ADD_VEHICLE, payload: vehicle });
};

export type RemoveVehicle = ({ make, model, yearMax, yearMin }: Partial<Users.VehiclePick>) => (
  dispatch: Dispatch<AnyAction>
) => {
  type: string;
  payload: {
    make: string;
    model: string;
    yearMax: string;
    yearMin: string;
  };
};
export const REMOVE_VEHICLE = "user.REMOVE_VEHICLE";
export const removeVehicle =
  ({ make, model, yearMax, yearMin }: Partial<Users.VehiclePick>) =>
  (dispatch: Dispatch) =>
    dispatch({
      payload: { make, model, yearMax, yearMin },
      type: REMOVE_VEHICLE,
    });

export type Reset = () => (dispatch: Dispatch<AnyAction>) => void;
export const RESET = "user.RESET";
export const reset = () => (dispatch: Dispatch) => {
  const {
    router: { rootPath },
  }: IState = store.getState();

  dispatch({ type: RESET });

  if (rootPath) {
    navigate(rootPath, { replace: true });
    location.reload(true);
  }
};

export const obscureMobile = (value) => {
  const valueArr = value.split("");
  valueArr[4] = "*";
  valueArr[5] = "*";
  valueArr[6] = "*";
  return valueArr.join("");
};

const isSignatureEnabled = () => {
  const { routes } = store.getState().router;

  if (routes && routes.signature && routes.signature.enabled) {
    return true;
  }

  return false;
};

const copyCheckInDataIntoEmptyUser = async (checkIn: Users.CheckIn): Promise<Users.User> => {
  // get the intersection of keys between checkIn and EmptyUser
  const keys = Object.keys(checkIn).filter((key) => key in emptyUser);
  const userData = { ...emptyUser };
  keys.forEach((key) => {
    userData[key] = checkIn[key];
  });

  const userDoc = await usersRef.where("mobile", "==", checkIn.mobile).limit(1).get();
  const firestoreUser = userDoc.docs[0].data() as Users.User;

  const convertedUser: Users.User = {
    ...userData,
    hasAccount: firestoreUser.hasAccount,
    language: firestoreUser.language,
    secretMobile: obscureMobile(checkIn.mobile),
    userId: firestoreUser.userId,
  };

  return convertedUser;
};

export const GET_USER = "user.GET_USER";
export const getUser = async (mobile: string, businessId: string): Promise<Users.User> => {
  try {
    const querySnapshot = await firestore
      .collection("checkIns")
      .where("mobile", "==", mobile)
      .where("businessId", "==", businessId)
      .orderBy("timestamp", "desc")
      .limit(1)
      .get();

    let user: Error | Users.User = new Error("user was not rewritten");

    if (querySnapshot.empty) {
      const userId = await usersRef.doc().id;

      user = {
        ...emptyUser,
        mobile,
        secretMobile: obscureMobile(mobile),
        userId,
      };
    } else {
      const doc = querySnapshot.docs[0];
      const userData: Users.User = await copyCheckInDataIntoEmptyUser(doc.data() as Users.CheckIn);

      const { hasAccount, language, secretMobile } = userData;

      // if not checked in before, user is {... emptyUser}
      // if user has checked into business before:
      // doc = checkIn Doc
      // user = { ..emptyUser, doc.forEach(not null field){field = doc.field.value }}
      // what else?

      user = {
        ...userData,
        hasAccount,
        language,
        secretMobile: secretMobile ? secretMobile : obscureMobile(mobile),
        userId: doc.id,
      };
    }

    if (isError(user)) {
      throw user;
    } else {
      // store.dispatch({ type: SET_LANGUAGE, payload: user.language || 'eng' });

      // TODO: This is a bandaid to avoid handling the routes correctly
      if (isSignatureEnabled()) {
        user.mugshot = null;
        user.mugshotUrl = null;
        user.signature = null;
        user.signatureUrl = null;
      }
      user.skipCheckIn = false;

      store.dispatch({ type: GET_USER, payload: user });

      return user;
    }
  } catch (error) {
    throw new Error(error);
  }
};

const getImageUrlPert = async (
  imageData: string,
  fileName: "mugshot.jpeg" | "signature.png",
  { businessId, randomStr, siteId, userId }
) => {
  if (imageData) {
    const imageRef = storage.ref(`/checkIns/${businessId}/${randomStr}/${fileName}`);

    try {
      const uploadSnapshot = await imageRef.putString(imageData, "data_url", {
        customMetadata: { businessId, siteId, userId },
      });

      const imageUrl: string = await uploadSnapshot.ref.getDownloadURL();

      return imageUrl;
    } catch (error) {
      throw new Error(error);
    }
  } else {
    return null;
  }
};

// if the user has already checked-in today, we want to skip additional data collection like name gathering.
export const SKIP_CHECK_IN = "user.SKIP_CHECK_IN";
export const GET_HAS_USER_PAID_TODAY = "user.GET_HAS_USER_PAID_TODAY";
export const getHasUserPaidToday =
  (
    mobile: string,
    businessId: string,
    siteId: string,
    sitePath: string,
    mugshot: any,
    signature: any,
    waiver: string | null
  ) =>
  async (dispatch: Dispatch<AnyAction>) => {
    await checkInsRef
      .where("mobile", "==", mobile)
      .where("businessId", "==", businessId)
      .where("siteId", "==", siteId)
      .orderBy("timestamp", "desc")
      .limit(1)
      .get()
      .then(async (snap) => {
        if (snap.size > 0) {
          const checkIn = snap.docs[0].data();

          // if user has checked into this yard today, mark it as a recheck.
          // if user has checked into another yard of this business today, mark it as a sister-yard-recheck
          // this is for analytics
          if (isToday(checkIn.timestamp.toDate())) {
            checkIn.kind = checkIn.siteId === siteId ? "recheck" : "sister-yard-recheck";
          } else {
            checkIn.kind = null;
          }

          // set this checkins timestamp to current time
          checkIn.timestamp = firebase.firestore.Timestamp.fromDate(new Date());

          // this makes sure that we pull in the most updated name possible
          // const userDoc = await firestore
          //   .collection("users")
          //   .doc(checkIn.userId)
          //   .get();

          const userId = checkIn.userId;
          const imagePromises: Array<Promise<string | null>> = [];

          const randomStr = "r" + Array.from(Array(20), () => Math.floor(Math.random() * 36).toString(36)).join("");

          if (mugshot) {
            imagePromises.push(
              getImageUrlPert(mugshot, "mugshot.jpeg", {
                businessId,
                randomStr,
                siteId,
                userId,
              })
            );
          }
          if (signature) {
            imagePromises.push(
              getImageUrlPert(signature, "signature.png", {
                businessId,
                randomStr,
                siteId,
                userId,
              })
            );
          }

          const [mugshotUrl, signatureUrl] = await Promise.all(imagePromises);

          checkIn.signature = null;
          checkIn.signatureUrl = signatureUrl || null;
          checkIn.mugshot = null;
          checkIn.mugshotUrl = mugshotUrl || null;
          checkIn.siteId = siteId;
          checkIn.sitePath = sitePath;
          checkIn.waiver = waiver;

          console.log("checkIn", checkIn);

          firestore.collection("checkIns").add(checkIn);
          dispatch({ type: SKIP_CHECK_IN, payload: true });
        }
      });
    return null;
  };

const getUserLoyaltyCycle = firebase.functions().httpsCallable("getUserLoyaltyCycle");

export const GET_LOYALTY_CYCLE_TEXT = "user.GET_LOYALTY_CYCLE_TEXT";
export const getLoyaltyCycleText =
  (businessId: string, siteId: string, language: string, mobile: string) => async (dispatch: Dispatch<AnyAction>) => {
    // const loyalty = await getUserLoyaltyCycle({
    //   businessId,
    //   language,
    //   mobile,
    //   siteId,
    // });
    // console.log("getUserLoyaltyCycle: ", loyalty);
    // if (loyalty.data) {
    //   dispatch({ type: GET_LOYALTY_CYCLE_TEXT, payload: loyalty.data });
    // } else {
    //   dispatch({ type: GET_LOYALTY_CYCLE_TEXT, payload: false });
    // }
  };

export function isError(value: any): value is Error {
  return (value as Error) instanceof Error;
}

export type SetUpdatingMode = (updating: boolean) => (dispatch: Dispatch<AnyAction>) => {
  type: string;
  payload: boolean;
};

export type GetHasUserPaidToday = (
  mobile: string,
  businessId: string,
  siteId: string,
  sitePath: string,
  mugshot: any,
  signature: any,
  waiver: string | null
) => (dispatch: Dispatch<AnyAction>) => void;

export type GetLoyaltyCycleText = (
  businessId: string,
  siteId: string,
  language: string,
  mobile: string
) => (dispatch: Dispatch<AnyAction>) => void;

export type RefreshCoupons = ({
  guid,
  mobile,
  userId,
}: {
  businessId: string;
  guid: string;
  mobile: string;
  userId: string;
}) => (dispatch: Dispatch<AnyAction>) => Promise<void | {
  type: string;
}>;
export const REFRESH_COUPONS = "user.REFRESH_COUPONS";
export const refreshCoupons =
  ({ businessId, guid, mobile, userId }: { businessId: string; guid: string; mobile: string; userId: string }) =>
  async (dispatch: Dispatch<AnyAction>) => {
    // const url = "https://us-central1-just-in-north.cloudfunctions.net/refreshCustomerCoupons";
    // try {
    //   const response = await fetch(url, {
    //     body: JSON.stringify({ businessId, guid, mobile, userId }),
    //     method: "post",
    //   });
    //   const text = await response.text();
    //   console.log("refreshCustomerCoupons", text);
    //   dispatch({ payload: JSON.stringify(text), type: REFRESH_COUPONS });
    // } catch (error) {
    //   throw new Error(error);
    // }
  };

const getUserRewardFire = firebase.functions().httpsCallable("getUserRewardFire");

export const GET_USER_REWARD_TEXT = "user.GET_USER_REWARD_TEXT";
export const getUserRewardText =
  (businessId: string, guid: string, siteId: string, language: string, mobile: string, userId: string) =>
  async (dispatch: Dispatch<AnyAction>) => {
    const reward = await getUserRewardFire({
      businessId,
      language,
      mobile,
      siteId,
    });

    console.log("getUserRewardFire", reward);

    if (reward.data) {
      dispatch({ type: GET_USER_REWARD_TEXT, payload: reward.data });
    } else {
      dispatch({ type: GET_USER_REWARD_TEXT, payload: false });
    }

    console.log("businessId: ", businessId);
    console.log("guid: ", guid);
    console.log("userId: ", userId);
    console.log("language: ", language);
    console.log("mobile: ", mobile);
    console.log("userId: ", userId);

    const loyalty = await getUserLoyaltyCycle({
      businessId,
      language,
      mobile,
      siteId,
    });

    console.log("getUserLoyaltyCycle: ", loyalty);
    if (loyalty.data) {
      dispatch({ type: GET_LOYALTY_CYCLE_TEXT, payload: loyalty.data });
    } else {
      dispatch({ type: GET_LOYALTY_CYCLE_TEXT, payload: false });
    }

    const url = "https://us-central1-just-in-north.cloudfunctions.net/refreshCustomerCoupons";

    try {
      const response = await fetch(url, {
        body: JSON.stringify({ businessId, guid, mobile, userId }),
        method: "post",
      });
      const text = await response.text();
      console.log("refreshCustomerCoupons", text);

      dispatch({ payload: JSON.stringify(text), type: REFRESH_COUPONS });
    } catch (error) {
      throw new Error(error);
    }
  };

export type GetUserRewardText = (
  businessId: string,
  guid: string,
  siteId: string,
  language: string,
  mobile: string,
  userId: string
) => (dispatch: Dispatch<AnyAction>) => void;



// WEBPACK FOOTER //
// ./src/store/actions/userActions.ts